import { FileSharer } from "@byteowls/capacitor-filesharer";
import { isDefined } from "@clipboard-health/util-ts";
import { isAndroidPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useMutation } from "@tanstack/react-query";
import { domToPng } from "modern-screenshot";

import { type ShareScoreEventPayload } from "./types";

interface UseShareClipboardScoreOptions {
  eventPayload: ShareScoreEventPayload;
}

interface ShareResult {
  shared: boolean;
  error?: string;
}

export function useShareClipboardScore(options: UseShareClipboardScoreOptions) {
  const { eventPayload } = options;
  const { showErrorToast } = useToast();

  return useMutation({
    mutationFn: async (): Promise<ShareResult> => {
      logEvent(APP_V2_USER_EVENTS.SHARE_CLIPBOARD_SCORE_SOCIAL_SHARE_TAPPED, {
        ...eventPayload,
      });

      try {
        const node = document.querySelector("#share-clipboard-score-card");

        if (!isDefined(node)) {
          return { shared: false, error: "Share card element not found" };
        }

        const dataUrl = await domToPng(node, {
          width: node.scrollWidth,
          height: node.scrollHeight,
          scale: window.devicePixelRatio,
          drawImageInterval: 500,
        });

        // Extract base64 data from data URL (remove "data:image/png;base64," prefix)
        // eslint-disable-next-line prefer-destructuring
        const base64Data = dataUrl.split(",")[1];

        try {
          await FileSharer.share({
            filename: "ClipboardScore.png",
            base64Data,
            contentType: "image/png",
          });
        } catch (shareError: unknown) {
          // Suppress "USER_CANCELLED" error on Android
          const shareErrorMessage =
            shareError instanceof Error ? shareError.message : String(shareError);

          if (isAndroidPlatform() && shareErrorMessage === "USER_CANCELLED") {
            return { shared: false };
          }

          throw shareError;
        }

        logEvent(APP_V2_USER_EVENTS.SHARE_CLIPBOARD_SCORE_SOCIAL_SHARE_SUCCESS, {
          ...eventPayload,
        });

        return { shared: true };
      } catch (maybeError: unknown) {
        const errorMessage = maybeError instanceof Error ? maybeError.message : String(maybeError);

        showErrorToast("We couldn't share your score. Please try again.");
        return {
          shared: false,
          error: errorMessage,
        };
      }
    },
  });
}
